import { ReactNode } from "react";
import { Link } from "react-router-dom";
import { cn } from "lib/utils";
import ScrollReveal from "components/ScrollReveal";

/* ── inline Marquee ─────────────────────────────────────────────── */
const Marquee = ({
  className = "",
  reverse = false,
  children,
  repeat = 4,
}: {
  className?: string;
  reverse?: boolean;
  children: ReactNode;
  repeat?: number;
}) => (
  <div className={cn("group flex select-none flex-row gap-8 overflow-hidden", className)}>
    {Array(repeat)
      .fill(0)
      .map((_, i) => (
        <div
          key={i}
          className={cn(
            "flex flex-shrink-0 flex-row gap-8",
            reverse ? "animate-marquee-horizontal-reverse" : "animate-marquee-horizontal"
          )}
        >
          {children}
        </div>
      ))}
  </div>
);

/* ── marquee badge ──────────────────────────────────────────────── */
const Badge = ({ icon, label }: { icon: string; label: string }) => (
  <div className="flex flex-shrink-0 items-center gap-2.5 rounded-full border border-brand-dark/15 bg-white px-5 py-2.5 shadow-sm">
    <span className="text-base">{icon}</span>
    <span className="whitespace-nowrap text-sm font-semibold uppercase tracking-widest text-brand-dark">
      {label}
    </span>
  </div>
);

const BADGES = [
  { icon: "🏆", label: "Award-Winning Aviary" },
  { icon: "📺", label: "AHA! Segment · GMA" },
  { icon: "⭐", label: "25+ Years of Excellence" },
  { icon: "🐦", label: "50+ Breeding Parrot Pairs" },
  { icon: "💛", label: "Family-Owned & Operated" },
  { icon: "🌿", label: "Responsible Breeding" },
  { icon: "🇵🇭", label: "Proudly Philippine" },
  { icon: "🏅", label: "National TV Featured" },
];

/* ── About ──────────────────────────────────────────────────────── */
const About = () => (
  <section className="bg-[#f7f5ef] py-20 md:py-28">

    {/* Top — large statement heading */}
    <div className="container mb-14">
      <ScrollReveal
        containerClassName="max-w-[680px]"
        textClassName="font-didot text-4xl leading-[1.12] text-brand-dark sm:text-5xl md:text-[3.25rem]"
        baseOpacity={0}
        baseRotation={5}
        blurStrength={10}
        rotationEnd="+=800"
        wordAnimationEnd="+=800"
      >
        Mysher Aviary is a family-owned bird sanctuary in the heart of the Philippines.
        For over 25 years, we've raised healthy, hand-raised parrots with unwavering
        dedication and love.
      </ScrollReveal>
    </div>

    {/* Full-width scrolling marquee */}
    <div className="border-y border-brand-dark/10 py-5">
      <Marquee>
        {BADGES.map((b) => (
          <Badge key={b.label} icon={b.icon} label={b.label} />
        ))}
      </Marquee>
    </div>

    {/* Bottom — secondary paragraph right-aligned */}
    <div className="container mt-14">
      <div className="ml-auto max-w-[520px]">
        <ScrollReveal
          textClassName="font-sf-pro text-lg leading-relaxed text-brand-dark/65 md:text-xl"
          baseOpacity={0}
          baseRotation={5}
          blurStrength={10}
          rotationEnd="+=800"
          wordAnimationEnd="+=800"
        >
          From a single pair of lovebirds in 2001 to over 50 breeding parrot pairs
          today, our story is built on decades of hands-on care, a passion for birds,
          and a deep commitment to every feathered life we raise.
        </ScrollReveal>
        <Link
          to="/birds"
          className="mt-6 inline-flex items-center gap-2 border-b border-brand-dark pb-0.5 text-sm font-bold uppercase tracking-[0.18em] text-brand-dark transition-opacity hover:opacity-60"
        >
          Our Story <span className="text-base">↗</span>
        </Link>
      </div>
    </div>

  </section>
);

export default About;
